import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import type { User, ReviewerContext } from './types';
import { invalidateCurrentUserStepCache } from './useCurrentUserStep';
import DefaultDmSettings from './DefaultDmSettings';
import './ContentReview.css';

interface ReviewerContextAdminProps {
  user: User | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const DOMAIN_OPTIONS = [
  { value: 'medical' as const, label: 'Medical' },
  { value: 'legal' as const, label: 'Legal' },
  { value: 'compliance' as const, label: 'Compliance' }
];

const ReviewerContextAdmin: React.FC<ReviewerContextAdminProps> = ({ user, getAuthHeaders }) => {
  const [contexts, setContexts] = useState<ReviewerContext[]>([]);
  const [teamMembers, setTeamMembers] = useState<{ _id: string; name: string; email: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newReviewer, setNewReviewer] = useState('');
  const [newDomain, setNewDomain] = useState<'medical' | 'legal' | 'compliance'>('medical');
  const [newContext, setNewContext] = useState('');
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const [ctxRes, teamRes] = await Promise.all([
        axios.get(`${backendUrl}/api/content-review/contexts`, { headers }),
        axios.get(`${backendUrl}/api/team`, { headers })
      ]);
      setContexts(ctxRes.data);
      setTeamMembers(teamRes.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveContext = async (id: string) => {
    try {
      setSaving(true);
      const headers = await getAuthHeaders();
      await axios.put(`${backendUrl}/api/content-review/contexts/${id}`, {
        standingContext: editText
      }, { headers });
      // WS1.1 — domain data changed; step-resolution cache must refetch.
      invalidateCurrentUserStepCache();
      setEditingId(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save context');
    } finally {
      setSaving(false);
    }
  };

  const handleMigrateHistory = async () => {
    if (!window.confirm('Ingest historical reviewer comments from the SharePoint archive workbook? This is idempotent — re-runs only add new rows.')) {
      return;
    }
    try {
      setMigrating(true);
      setMigrateResult(null);
      setError(null);
      const headers = await getAuthHeaders();
      const res = await axios.post(`${backendUrl}/api/content-review/admin/migrate-history`, {}, { headers });
      const { inserted, skipped, byDomain, sheet, sourceFile } = res.data;
      const domainSummary = Object.entries(byDomain || {}).map(([d, n]) => `${d}: ${n}`).join(', ');
      setMigrateResult(
        `Migrated from "${sourceFile}" (sheet: ${sheet}). Inserted ${inserted} new patterns (${skipped} skipped as duplicates). Domain totals — ${domainSummary}.`
      );
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  const handleCreateContext = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReviewer) return;

    try {
      setSaving(true);
      const headers = await getAuthHeaders();
      await axios.post(`${backendUrl}/api/content-review/contexts`, {
        reviewer: newReviewer,
        domain: newDomain,
        standingContext: newContext.trim()
      }, { headers });
      // WS1.1 — a new reviewer domain exists; step-resolution cache must refetch.
      invalidateCurrentUserStepCache();
      setShowCreate(false);
      setNewReviewer('');
      setNewContext('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create context');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Loading reviewer contexts...</div>;

  return (
    // Shares the taxonomy-admin scoped control styling (walk sidenote,
    // 2026-08-13) — registry-area selects/inputs match the site look.
    <div className="taxonomy-admin">
      {/* DM routing (2026-09-02) — module config sits with the other
          review-role config, not in Team Settings. */}
      <DefaultDmSettings getAuthHeaders={getAuthHeaders} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Reviewer Standing Contexts</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="review-btn review-btn-outline"
            onClick={handleMigrateHistory}
            disabled={migrating}
            title="Ingest historical reviewer comments from the SharePoint archive workbook into the AI pre-review knowledge base. Idempotent — safe to re-run."
          >
            {migrating ? 'Migrating…' : 'Migrate History from SharePoint'}
          </button>
          <button className="review-btn review-btn-primary" onClick={() => setShowCreate(true)}>
            + Add Context
          </button>
        </div>
      </div>

      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Standing contexts inform the AI pre-review service about each reviewer's domain expertise and what they typically look for.
      </p>

      {migrateResult && (
        <div style={{ color: '#0a7d3b', padding: 12, background: '#e6f5ec', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {migrateResult}
        </div>
      )}
      {error && <div style={{ color: '#dc3545', padding: 12, background: '#f8d7da', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {contexts.length === 0 ? (
        <div className="review-empty">
          <p>No reviewer contexts configured yet.</p>
          <button className="review-btn review-btn-primary" onClick={() => setShowCreate(true)}>
            Add First Context
          </button>
        </div>
      ) : (
        contexts.map(ctx => (
          <div key={ctx._id} className="context-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: '#123250' }}>
                  {ctx.reviewer.name}
                </span>
                <span className={`context-domain-badge ${ctx.domain}`}>{ctx.domain}</span>
              </div>
              {editingId !== ctx._id && (
                <button
                  className="review-btn review-btn-outline"
                  style={{ padding: '6px 14px', fontSize: 13 }}
                  onClick={() => { setEditingId(ctx._id); setEditText(ctx.standingContext); }}
                >
                  Edit
                </button>
              )}
            </div>

            {editingId === ctx._id ? (
              <div>
                <textarea
                  className="review-form-group"
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  style={{
                    width: '100%', minHeight: 100, padding: '10px 12px',
                    border: '1px solid #d0d0d0', borderRadius: 8, fontSize: 14,
                    fontFamily: 'Inter, sans-serif', boxSizing: 'border-box'
                  }}
                  placeholder="Describe what this reviewer typically checks, their expertise, focus areas..."
                />
                <div className="review-action-bar" style={{ marginTop: 10 }}>
                  <button className="review-btn review-btn-primary" onClick={() => handleSaveContext(ctx._id)} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button className="review-btn review-btn-outline" onClick={() => setEditingId(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <p style={{ fontSize: 14, color: '#444', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' }}>
                {ctx.standingContext || <span style={{ color: '#999', fontStyle: 'italic' }}>No standing context set.</span>}
              </p>
            )}

          </div>
        ))
      )}

      {/* Create new context */}
      {showCreate && (
        <div className="review-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="review-modal" onClick={e => e.stopPropagation()}>
            <h3>Add Reviewer Context</h3>
            <form onSubmit={handleCreateContext}>
              <div className="review-form-group">
                <label>Reviewer *</label>
                <select value={newReviewer} onChange={e => setNewReviewer(e.target.value)}>
                  <option value="">Select reviewer...</option>
                  {teamMembers.map(m => (
                    <option key={m._id} value={m._id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div className="review-form-group">
                <label>Domain *</label>
                <select value={newDomain} onChange={e => setNewDomain(e.target.value as any)}>
                  {DOMAIN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="review-form-group">
                <label>Standing Context</label>
                <textarea
                  value={newContext}
                  onChange={e => setNewContext(e.target.value)}
                  placeholder="Describe what this reviewer typically checks, their expertise, focus areas..."
                  rows={4}
                />
              </div>
              {error && <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 12 }}>{error}</div>}
              <div className="review-action-bar">
                <button type="submit" className="review-btn review-btn-primary" disabled={!newReviewer || saving}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
                <button type="button" className="review-btn review-btn-outline" onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewerContextAdmin;
